<template>
  <common-modal
    :name="name"
    :title="title"
    :primaryAction="importModalPrimaryAction"
    :onClosed="onModalClosed"
    :disabledActions="importDisabledActions"
    height="auto"
    width="60%"
  >
    <template #modal-body>
      <slot name="template-desc"></slot>

      <vue-dropzone
        ref="filesZone"
        id="csvImportZone"
        :options="dropzoneOptions"
        :useCustomSlot="true"
        @vdropzone-file-added="addedFile"
        v-show="!file"
      >
        <div class="isis-dropzone-content">
          <h3 class="dropzone-custom-title">
            <i class="fas fa-upload"></i>
          </h3>
          <h4 class="title">{{ $t("import_csv.add_file") }}</h4>
          <div class="subtitle">
            {{ $t("import_csv.or_drop_file_to_upload") }}
          </div>
        </div>
      </vue-dropzone>

      <div class="isis-file-preview" v-if="file">
        <div class="file-infos-row">
          <div class="file-infos">
            <div class="logo"><i class="fas fa-file-csv"></i></div>
            <p>{{ file.name }}</p>
          </div>
          <md-button @click="onReplaceFile" class="replace-file-btn">
            {{ $t("import_csv.replace_file") }}
          </md-button>
        </div>

        <div class="preview-table-data">
          <vue-excel-editor
            v-model="jsonFileData"
            :readonly="true"
            :no-header-edit="true"
            v-if="!!jsonFileData"
            ref="exelEditor"
            :page="page"
            :height="tableHeight"
            disable-panel-filter
            disable-panel-setting
          >
            <vue-excel-column
              v-for="header in headerTableData"
              :key="header"
              type="string"
              :field="header"
              :label="header"
              :validate="validateImportedCsvColumn"
              readonly
            />
          </vue-excel-editor>
        </div>
      </div>
    </template>
  </common-modal>
</template>

<script>
import CommonModal from "@/components/Base/CommonModal";
import { generateInvalidMessage } from "@/validations/common";
import { NotificationStore } from "@/components/NotificationPlugin";
import vue2Dropzone from "vue2-dropzone";
import Papa from "papaparse";
import cloneDeep from "lodash/cloneDeep";

const initialFileData = [{}];

export default {
  components: { CommonModal, vueDropzone: vue2Dropzone },
  props: [
    ...CommonModal.props,
    "actions",
    "validationCols",
    "formatImportedData"
  ],
  data() {
    return {
      file: null, // to keep added file,
      invalidFile: true,
      jsonFileData: initialFileData,
      dropzoneOptions: {
        url: "https://examle.org/post",
        thumbnailWidth: 150,
        maxFilesize: 0.5,
        acceptedFiles: ".csv",
        maxFiles: 1,
        autoProcessQueue: false, // not-allow auto send file. Ref: https://www.dropzonejs.com/#config-autoProcessQueue
        previewTemplate: this.genPreviewTemplate()
      },
      page: null,
      tableHeight: "auto"
    };
  },
  computed: {
    importDisabledActions: function() {
      return !this.file || (this.file && this.invalidFile);
    },

    headerTableData() {
      return this.jsonFileData
        ? Object.keys(this.jsonFileData[0]).filter(colName => colName !== "$id")
        : [];
    },

    importModalPrimaryAction() {
      const propsPrimaryAction = this.actions.primary;

      return {
        ...propsPrimaryAction,
        action: () => {
          this.actions.primary.action && this.actions.primary.action(this.file);
        }
      };
    }
  },
  methods: {
    genPreviewTemplate: function() {
      return `<div class="dz-preview dz-file-preview"></div>`;
    },
    addedFile(file) {
      // remove old file from dropzone
      this.file && this.$refs.filesZone.removeFile(this.file);

      setTimeout(() => {
        console.log("added file: ", file);

        if (file.status === "error") {
          const message = !file.accepted
            ? "Not accepted your file"
            : "Something wen't wrong";

          NotificationStore.notify({
            message,
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger"
          });

          this.$refs.filesZone.removeFile(file);
          return;
        }

        this.setNewFileData(file);
      }, 1);
    },

    onReplaceFile() {
      this.$refs.filesZone.$refs.dropzoneElement.click();
    },

    setNewFileData(file) {
      const self = this;
      this.file = file;
      Papa.parse(file, {
        header: true,
        complete: results => {
          self.invalidFile = false;
          if (this.formatImportedData) {
            self.jsonFileData = cloneDeep(
              self.removeEmptyData(self.formatImportedData(results.data))
            );
          } else
            self.jsonFileData = cloneDeep(self.removeEmptyData(results.data));
          self.setNewTableStyles(results.data);
          // waiting after import data
          setTimeout(() => {
            self.triggerValidateWhenChangeData(cloneDeep(results.data));
          }, 300);
        }
      });
    },

    removeEmptyData(jsonFileData) {
      return jsonFileData.filter(record =>
        Object.values(record).some(
          fieldValue => !!fieldValue && !!fieldValue.length
        )
      );
    },

    onModalClosed() {
      if (this.file) {
        this.file = null;
        this.jsonFileData = initialFileData;
      }
    },

    // refer validation: https://www.npmjs.com/package/vue-excel-editor
    validateImportedCsvColumn(content, oldContent, record, field) {
      const colName = field.label;

      if (!this.validationCols) return "";

      const { fields, validations } = this.validationCols;

      if (fields && !fields.includes(colName)) {
        this.invalidFile = true;
        return `The field ${colName} is not accepted.`;
      }

      const invalidMsg = generateInvalidMessage(
        validations[colName],
        colName,
        content
      );

      invalidMsg && (this.invalidFile = true);

      return invalidMsg;
    },

    // trigger by re-assign value
    triggerValidateWhenChangeData(newData) {
      const self = this;

      newData.forEach((objData, rowIdx) => {
        Object.keys(objData).forEach(async colName => {
          // skip $id column - this is additional column by vue-excel-editor
          if (colName !== "$id") {
            try {
              await self.$refs.exelEditor.updateCell(
                rowIdx,
                colName,
                objData[colName]
              );
            } catch (error) {
              // console.error(
              //   "error triggerValidateWhenChangeData: ",
              //   rowIdx,
              //   colName
              // );
            }
          }
        });
      });
    },

    setNewTableStyles(tableData) {
      if (tableData.length >= 17) {
        this.page = 17;
        this.tableHeight = "46vh";
        return;
      }
      this.page = null;
      this.tableHeight = "auto";
    }
  }
};
</script>

<style lang="scss">
// .isis-modal {
//   height: fit-content !important;
// }

.isis-file-preview {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .file-infos-row {
    display: flex;
    margin-bottom: 15px;
    .file-infos {
      display: flex;
      align-items: center;

      & > * {
        display: inline-block;
        font-size: 1rem;
      }
      .logo {
        margin-right: 10px;

        i {
          font-size: 1.25rem;
        }
      }
    }
  }

  // .table-content {
  //   max-height: 46vh;
  // }

  .replace-file-btn {
    margin-left: auto;
  }
}

#csvImportZone {
  margin-top: 15px;

  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: #f6f6f7;
  color: #5c5f62;

  border-style: dashed;
  border-width: 2px;
  border-color: #8c9196;
  border-radius: 4px;

  font-size: 14px;
  text-align: center;
  cursor: pointer;

  &.dz-started {
    .dz-message {
      display: block !important;
    }
  }

  .isis-dropzone-content {
    display: flex;
    flex-direction: column;

    & > * {
      margin: 0px;
    }

    & > * ~ * {
      margin-top: 8px;
    }
  }

  .dz-preview {
    display: none !important;
  }
}
</style>
